import { useGetCoInvestGpPermissions } from "@vega-api/generated/coinvest-gp-permissions/coinvest-gp-permissions";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import {
  ActionMenu,
  type MenuItem,
} from "@vega-core/components/ActionMenu/ActionMenu";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";

import { RevisedNdaUploadModal } from "../../RevisedNdaUploadModal/RevisedNdaUploadModal";
import { hasUnsignedNdas } from "../utils/ndaHelpers";

import { CloseDealModal } from "./CloseDealModal";
import { ConfirmNdaSignedModal } from "./ConfirmNdaSignedModal";
import { RejectDealModal } from "./RejectDealModal";
import { UpdateDataPointsModal } from "./UpdateDataPointsModal";

type TrackingRowActionsMenuProps = {
  trackingItem: DealTrackingLimitedPartnerBreakdown;
  dealId: string;
  /**
   * Whether to offer the "Confirm NDA signed" action. The legacy tables keep
   * it (default `true`); the new AG-Grid Process table sets `false` because NDA
   * status is edited inline in the NDA column there.
   */
  showConfirmNdaSigned?: boolean;
};

export const TrackingRowActionsMenu = ({
  trackingItem,
  dealId,
  showConfirmNdaSigned = true,
}: TrackingRowActionsMenuProps) => {
  const { data: permissions } = useGetCoInvestGpPermissions(
    { dealId },
    {
      query: {
        select: (response) => response.data,
      },
    },
  );
  const canUploadDocuments = permissions?.canUploadDealDocuments;
  const canEditDealOpportunity = permissions?.canEditDealOpportunity;
  const dealOpportunityId = trackingItem.dealOpportunityId;

  const {
    isOpen: isNdaModalOpen,
    setTrue: openNdaModal,
    setFalse: closeNdaModal,
  } = useBoolean(false);

  const {
    isOpen: isRevisedNdaModalOpen,
    setTrue: openRevisedNdaModal,
    setFalse: closeRevisedNdaModal,
  } = useBoolean(false);

  const {
    isOpen: isRejectDealModalOpen,
    setTrue: openRejectDealModal,
    setFalse: closeRejectDealModal,
  } = useBoolean(false);

  const {
    isOpen: isUpdateDataPointsModalOpen,
    setTrue: openUpdateDataPointsModal,
    setFalse: closeUpdateDataPointsModal,
  } = useBoolean(false);

  const {
    isOpen: isCloseDealModalOpen,
    setTrue: openCloseDealModal,
    setFalse: closeCloseDealModal,
  } = useBoolean(false);

  const menuItems: MenuItem[] = [
    ...(buildTimeFlags.gp_co_investment_update_salesforce_data &&
    canEditDealOpportunity
      ? [
          {
            title: m.coInvestments_tracking_dataPoints_action(),
            onClick: openUpdateDataPointsModal,
            dataTestId: "update-salesforce-data",
          },
        ]
      : []),
    ...(showConfirmNdaSigned &&
    canEditDealOpportunity &&
    !trackingItem.isRejected &&
    !trackingItem.isClosed &&
    hasUnsignedNdas(trackingItem)
      ? [
          {
            title: m.coInvestments_tracking_confirmNdaSigned(),
            onClick: openNdaModal,
            dataTestId: "confirm-nda-signed",
          },
        ]
      : []),
    ...(canEditDealOpportunity &&
    !trackingItem.isRejected &&
    !trackingItem.isClosed &&
    !!trackingItem.commitmentAmount
      ? [
          {
            title: m.coInvestments_tracking_closeDeal(),
            onClick: openCloseDealModal,
            dataTestId: "close-deal",
          },
        ]
      : []),
    ...(canUploadDocuments && !trackingItem.isClosed
      ? [
          {
            title: m.coInvestments_tracking_addRevisedNda(),
            onClick: openRevisedNdaModal,
            dataTestId: "add-revised-nda",
          },
        ]
      : []),
    ...(canEditDealOpportunity &&
    !trackingItem.isRejected &&
    !trackingItem.isClosed
      ? [
          {
            title: m.coInvestments_tracking_declineDeal(),
            onClick: openRejectDealModal,
            dataTestId: "reject-deal",
          },
        ]
      : []),
  ];

  return (
    <>
      <ActionMenu
        className="w-full h-full flex items-center justify-center"
        dataTestId="tracking-actions-menu"
        menuItems={menuItems}
        variant="tertiary"
      />
      <ConfirmNdaSignedModal
        dealId={dealId}
        isOpen={isNdaModalOpen}
        onClose={closeNdaModal}
        trackingItem={trackingItem}
      />

      <RevisedNdaUploadModal
        dealId={dealId}
        isOpen={isRevisedNdaModalOpen}
        limitedPartnerId={trackingItem.limitedPartnerId}
        onClose={closeRevisedNdaModal}
      />

      <RejectDealModal
        dealId={dealId}
        dealOpportunityId={dealOpportunityId}
        isOpen={isRejectDealModalOpen}
        onClose={closeRejectDealModal}
      />

      {isUpdateDataPointsModalOpen ? (
        <UpdateDataPointsModal
          dealId={dealId}
          dealOpportunityId={dealOpportunityId}
          isOpen={isUpdateDataPointsModalOpen}
          onClose={closeUpdateDataPointsModal}
        />
      ) : null}

      <CloseDealModal
        dealId={dealId}
        dealOpportunityId={dealOpportunityId}
        isOpen={isCloseDealModalOpen}
        onClose={closeCloseDealModal}
      />
    </>
  );
};
